/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-30
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Assignment as RequestIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Add local dependencies.
import Header from '../../layout/Header';
import PublicStudioBrowser from './PublicStudioBrowser';
import MyJoinRequests from '../../studios/joinRequests/MyJoinRequests';

/**
 * AddStudio page component that provides options to browse public studios
 * or create a new studio, along with managing join requests.
 */
const AddStudio: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'requests' ? 1 : 0;
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleCreateStudio = () => {
    navigate('/studios/create');
  };

  const handleBack = () => {
    navigate('/studios');
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleJoinRequestSuccess = () => {
    // Optionally switch to the requests tab to show the new request
    setActiveTab(1);
  };

  return (
    <>
      <Header />
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <Button
                startIcon={<BackIcon />}
                onClick={handleBack}
                variant="text"
                color="inherit"
              >
                Back to Studios
              </Button>
            </Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Add Studio
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Join an existing studio or create your own
            </Typography>
          </Box>

          {/* Create Studio Card */}
          <Card sx={{ mb: 4, bgcolor: 'primary.50' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Create Your Own Studio
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start fresh with your own dance studio community
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleCreateStudio}
                >
                  Create New Studio
                </Button>
              </Box>
            </CardContent>
          </Card>

          <Divider sx={{ my: 4 }}>
            <Typography variant="body2" color="text.secondary">
              OR
            </Typography>
          </Divider>

          {/* Tabs for Browse and Requests */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="add studio tabs"
            >
              <Tab
                icon={<SearchIcon />}
                label="Browse Studios"
                iconPosition="start"
              />
              <Tab
                icon={<RequestIcon />}
                label="My Join Requests"
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {activeTab === 0 && (
            <PublicStudioBrowser
              onJoinRequestSuccess={handleJoinRequestSuccess}
            />
          )}

          {activeTab === 1 && (
            <MyJoinRequests
              onCancel={() => {
                // Refresh could be handled here if needed
              }}
              // Switch to the browse tab to send a new request
              onResubmit={() => setActiveTab(0)}
              onRequestToJoin={() => setActiveTab(0)}
            />
          )}
        </Box>
      </Container>
    </>
  );
};

export default AddStudio;
